/* ============================================================
   earningsView — CB-4 (TASK-cf5f4532): pure view model for the
   provider cabinet «Доходы» tab. Derives from the read-only
   earnings payload only:
   - formatRub: «5 000 ₽» (ru-RU grouping, no fake decimals)
   - stateLabel/stateTone: money-state badges (эскроу/выплачено/
     возвращено) — tone maps to existing light-theme tokens
   - groupByMonth: monthly aggregates over per-order rows
     (cabinet-gap-audit §5 P4 ask)
   - paidDate: «27.08» short date for rows

   No fake data; no API calls. Light theme, Montserrat, iOS-18
   minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { ProviderEarnings, ProviderEarningsRow, ProviderEarningsState } from '../types/provider';

/** Rubles with ru-RU space grouping; NBSP normalized to a plain space; NaN/Infinity render as 0. */
export function formatRub(rub: number): string {
  const value = Number.isFinite(rub) ? rub : 0;
  return `${group(value)} ₽`;
}

/** Short money for dense tables — «12 500» without the ₽ glyph. */
export function formatRubPlain(rub: number): string {
  return group(Number.isFinite(rub) ? rub : 0);
}

function group(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 0 }).replace(/\u00A0/g, ' ');
}

/** Where the money sits, in Russian. */
export function stateLabel(state: ProviderEarningsState): string {
  switch (state) {
    case 'PaidOut': return 'Выплачено';
    case 'Refunded': return 'Возвращено';
    default: return 'В эскроу';
  }
}

/** Badge tone per state: success / warning / danger (light tokens). */
export function stateTone(state: ProviderEarningsState): 'success' | 'warning' | 'danger' {
  switch (state) {
    case 'PaidOut': return 'success';
    case 'Refunded': return 'danger';
    default: return 'warning';
  }
}

/** Row-level hint when a payout attempt is in flight. */
export function payoutHint(row: ProviderEarningsRow): string | null {
  if (row.payoutPending) return 'Выплата в пути';
  if (row.state === 'InEscrow') return 'Ждёт выплаты';
  return null;
}

export interface EarningsMonthGroup {
  /** ISO month key, e.g. '2026-08'. */
  key: string;
  /** «Август 2026». */
  label: string;
  count: number;
  grossRub: number;
  feeRub: number;
  netRub: number;
}

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

/** Monthly aggregates over the paid-at stamp, newest month first. */
export function groupByMonth(rows: ProviderEarningsRow[], now = new Date()): EarningsMonthGroup[] {
  const byKey = new Map<string, EarningsMonthGroup>();
  for (const row of rows) {
    const paid = new Date(row.paidAtUtc);
    if (Number.isNaN(paid.getTime())) continue;
    const key = `${paid.getUTCFullYear()}-${String(paid.getUTCMonth() + 1).padStart(2, '0')}`;
    let group = byKey.get(key);
    if (!group) {
      const sameYear = paid.getUTCFullYear() === now.getUTCFullYear();
      group = {
        key,
        label: `${MONTHS_RU[paid.getUTCMonth()]}${sameYear ? '' : ` ${paid.getUTCFullYear()}`}`,
        count: 0,
        grossRub: 0,
        feeRub: 0,
        netRub: 0,
      };
      byKey.set(key, group);
    }
    group.count += 1;
    group.grossRub += row.grossRub;
    group.feeRub += row.platformFeeRub;
    group.netRub += row.netRub;
  }
  return [...byKey.values()].sort((a, b) => b.key.localeCompare(a.key));
}

/** «27.08» — paid-at day for the row table. */
export function paidDate(row: ProviderEarningsRow): string {
  const paid = new Date(row.paidAtUtc);
  if (Number.isNaN(paid.getTime())) return '—';
  return paid.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

/** True when the provider has zero Safe-Deal-paid orders — legacy «без Safe Deal» empty state. */
export function isEmptyEarnings(earnings: ProviderEarnings | null): boolean {
  return !earnings || earnings.paidOrders === 0;
}
